import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import PageLayout from "../../components/common/page-layout";
import { type SmsConsentAdminFilterInput } from "../../generated/graphql";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import {
  COLUMN_HEADERS,
  type LineTypeFilter,
  NO_RECORD_MESSAGE,
  SMS_CONSENT_COLUMN_COUNT,
  landlineFilterValue,
} from "./sms-consent-display";
import SmsConsentFilters, { type StatusFilter } from "./sms-consent-filters";
import SmsConsentRow from "./sms-consent-row";
import useSmsConsentPages from "./use-sms-consent-pages";

const TITLE = "SMS consent";
const SUBTITLE =
  "Every number that has received a consignee text or used the opt-in form, newest first. Driver texts are not tracked here. Numbers show only their last four digits; search by the full number to find one. Every view is logged.";

const styles = {
  page: { p: 3 } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 4,
  } satisfies SxProps<Theme>,
  loadMore: { mt: 2 } satisfies SxProps<Theme>,
};

/**
 * Support's view of SMS consent: whether a number may be texted and how it
 * got there. A carrier-provided number has no opt-in date, because nobody
 * opted it in; it is texted as the carrier's service provider until it opts
 * out.
 */
const SmsConsentAdminPage = () => {
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();

  const [searchedNumber, setSearchedNumber] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>("ALL");
  const [lineType, setLineType] = useState<LineTypeFilter>("ALL");

  const filter = useMemo<SmsConsentAdminFilterInput>(
    () => ({
      phoneNumber: searchedNumber,
      status: status === "ALL" ? null : status,
      landline: landlineFilterValue(lineType),
    }),
    [lineType, searchedNumber, status],
  );

  const {
    rows,
    error,
    loading,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
  } = useSmsConsentPages(filter);

  // "No record" is only true when nothing but the number narrowed the search:
  // a status or line type filter can hide a row that does exist.
  const isUnfilteredNumberSearch =
    searchedNumber !== null && status === "ALL" && lineType === "ALL";

  const onLoadMore = useCallback(() => {
    void loadMore();
  }, [loadMore]);

  if (permissionsLoading) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.page}>
          <Alert severity="error">
            You need the INTERNAL_TOOLS write permission to use this tool.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout subtitle={SUBTITLE} title={TITLE}>
      <Box sx={styles.page}>
        <SmsConsentFilters
          lineType={lineType}
          searchedNumber={searchedNumber}
          status={status}
          onLineTypeChange={setLineType}
          onSearchedNumberChange={setSearchedNumber}
          onStatusChange={setStatus}
        />

        {error !== undefined && (
          <Alert severity="error">
            Could not load SMS consent. {error.message}
          </Alert>
        )}

        {error === undefined && loading && (
          <Box sx={styles.centered}>
            <CircularProgress />
          </Box>
        )}

        {error === undefined && !loading && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {COLUMN_HEADERS.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={SMS_CONSENT_COLUMN_COUNT}>
                      {isUnfilteredNumberSearch
                        ? NO_RECORD_MESSAGE
                        : "No numbers match these filters."}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <SmsConsentRow key={row.uuid} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {loadMoreError !== null && (
          <Alert severity="error">
            Could not load more numbers. {loadMoreError}
          </Alert>
        )}

        {hasMore && (
          <Button
            disabled={loadingMore}
            sx={styles.loadMore}
            variant="outlined"
            onClick={onLoadMore}
          >
            {loadingMore ? "Loading..." : "Load more"}
          </Button>
        )}
      </Box>
    </PageLayout>
  );
};

export default SmsConsentAdminPage;
